import type { NumericTextBoxChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { RangeSlider } from "@tql/ui-components-react/kendo/inputs";
import { Label } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { NumericTextBox } from "@tql/ui-components-react/tql/inputs/beta";
import { useCallback, useEffect, useState } from "react";

import {
	SliderFilter,
	SliderFilterActions,
	SliderFilterInput,
	SliderFilterInputs,
} from "../slider-filter/slider-filter.tsx";

type TransitDaysFilterProps = {
	min: number;
	max: number;
	onChange: (value: number[]) => void;
};

type SliderRange = {
	end: number;
	selectionEnd?: number;
	selectionStart?: number;
	start: number;
};

type RangeSliderChangeEvent = {
	value: SliderRange;
};

const getOrderedRange = (start: number, end: number): [number, number] => {
	return start <= end ? [start, end] : [end, start];
};

const getDaysInRange = ([start, end]: [number, number]): number[] => {
	const days: number[] = [];

	for (let day = start; day <= end; day += 1) {
		days.push(day);
	}

	return days;
};

const clamp = (value: number, minValue: number, maxValue: number): number => {
	return Math.min(Math.max(value, minValue), maxValue);
};

const normalizeTransitDay = (
	value: number,
	minValue: number,
	maxValue: number,
): number => {
	return clamp(Math.round(value), minValue, maxValue);
};

export const TransitDaysFilter = ({
	min,
	max,
	onChange,
}: TransitDaysFilterProps) => {
	const [selectedRange, setSelectedRange] = useState<[number, number] | null>(
		null,
	);
	const normalizedMin = Math.round(min);
	const normalizedMax = Math.round(max);
	const hasValidRange =
		Number.isFinite(min) &&
		Number.isFinite(max) &&
		normalizedMin < normalizedMax;
	const currentRange = selectedRange ?? [normalizedMin, normalizedMax];

	const getSelectedRangeText = (): string => {
		if (!selectedRange) {
			return "";
		}

		if (selectedRange[0] === selectedRange[1]) {
			return `(${selectedRange[0]})`;
		}

		return `(${selectedRange[0]} - ${selectedRange[1]})`;
	};

	const selectedRangeText = getSelectedRangeText();

	useEffect(() => {
		setSelectedRange(null);
	}, [min, max]);

	const updateSelectedRange = useCallback(
		(range: [number, number]) => {
			const orderedRange = getOrderedRange(
				normalizeTransitDay(range[0], normalizedMin, normalizedMax),
				normalizeTransitDay(range[1], normalizedMin, normalizedMax),
			);

			setSelectedRange(orderedRange);
			onChange(getDaysInRange(orderedRange));
		},
		[normalizedMax, normalizedMin, onChange],
	);

	const handleSliderChange = (event: RangeSliderChangeEvent) => {
		updateSelectedRange([event.value.start, event.value.end]);
	};

	const handleMinChange = (event: NumericTextBoxChangeEvent) => {
		if (event.value === null) {
			return;
		}

		const nextMin = normalizeTransitDay(
			event.value,
			normalizedMin,
			currentRange[1],
		);
		updateSelectedRange([nextMin, currentRange[1]]);
	};

	const handleMaxChange = (event: NumericTextBoxChangeEvent) => {
		if (event.value === null) {
			return;
		}

		const nextMax = normalizeTransitDay(
			event.value,
			currentRange[0],
			normalizedMax,
		);
		updateSelectedRange([currentRange[0], nextMax]);
	};

	return (
		<SliderFilter
			hasValidRange={hasValidRange}
			label={"Transit Days"}
			popupTestId={"transit-days-popup"}
			selectedText={selectedRangeText}
			triggerTestId={"transit-days"}
		>
			{({ closePopup }) => {
				const handleReset = () => {
					setSelectedRange(null);
					onChange([]);
					closePopup();
				};

				return (
					<>
						<RangeSlider
							largeStep={1}
							max={normalizedMax}
							min={normalizedMin}
							onChange={handleSliderChange}
							step={1}
							style={{ width: "100%" }}
							value={{ start: currentRange[0], end: currentRange[1] }}
						/>
						<SliderFilterInputs>
							<SliderFilterInput>
								<Label editorId={"transitDaysFilterMin"}>Min</Label>
								<NumericTextBox
									id={"transitDaysFilterMin"}
									max={currentRange[1]}
									min={normalizedMin}
									onChange={handleMinChange}
									spinners={false}
									value={currentRange[0]}
									width={70}
								/>
							</SliderFilterInput>

							<SliderFilterInput>
								<Label editorId={"transitDaysFilterMax"}>Max</Label>
								<NumericTextBox
									id={"transitDaysFilterMax"}
									max={normalizedMax}
									min={currentRange[0]}
									onChange={handleMaxChange}
									spinners={false}
									value={currentRange[1]}
									width={70}
								/>
							</SliderFilterInput>
						</SliderFilterInputs>
						<SliderFilterActions>
							<Button onClick={handleReset}>Reset</Button>
						</SliderFilterActions>
					</>
				);
			}}
		</SliderFilter>
	);
};
